// Dashboard KPI aggregation, ported from the original renderDashboard logic
// (rule-engine reference §7) and mapped to TK's brief: total transactions,
// mechanism/type breakdown, # reviewed, # compliant, and possible-IE exposure
// as BOTH a count and a Rand value-at-risk, plus a weighted risk score.

import type { Transaction } from '../data/types';
import { isSelfDeclaredLevel } from './gaps';
import { canonicalDivision } from './divisions';

// IE/high tier. Carries the original Possible-IE levels plus the newer
// "Non-Conformance" taxonomy (NEW_COMPLIANCE_LEVELS): a bare Non-Conformance —
// including its Administrative Control Weakness variant and the self-declared
// form — is the new register's equivalent of a possible irregular expenditure,
// so it weights the same (3).
// Tier membership follows the NTCSA-legend authority (domain/workingPapers/
// terminology.ts) — corrected on the 9 Jun alignment call (the previous mapping
// had the newer taxonomy inverted): Possible Non-Compliance is the PFMA-anchored
// most-severe level; bare Non-Conformance is the policy tier; Administrative
// Control Weakness is housekeeping.
const IE_LEVELS = new Set([
  'Non-Compliance: Possible IE',
  'Self-Declared: Non-Compliance: Possible IE',
  'Possible Non-Compliance',
  'Self-Declared: Possible Non-Compliance',
]);
const POLICY_LEVELS = new Set([
  'Non-Compliant: Policy & Procedures',
  'Self-Declared: Non-Compliant: Policy & Procedures',
  'Non-Conformance',
  'Self-Declared: Non-Conformance',
]);
const HK_LEVELS = new Set([
  'Non-Compliance Housekeeping',
  'Self-Declared: Non-Compliance Housekeeping',
  'Non-Conformance - Administrative Control Weakness',
]);
const PENDING_LEVELS = new Set(['Pending Review', 'Not Reviewed']);
// Reviewed-but-not-an-exception outcome: neither compliant nor a finding. Kept as
// its own bucket so the outcome tiles are exhaustive and reconcile with Population.
export const NA_LEVELS = new Set(['Not Applicable', 'Self-Declared: Not Applicable']);
// Lifecycle sentinels — a reviewer has flagged the row for removal. NOT a verdict:
// they carry no risk weight and must NEVER count as Compliant (they were silently
// swept into Compliant before 15 Sep 2026, so the dashboard read 2 higher than the
// report, which isolates the same rows in `unclassified`). Reported under the
// "Anomalies" umbrella beside Not Applicable (Lindiwe, 15 Sep 2026).
export const DELETE_SENTINEL_LEVELS = new Set(['Deleted', 'To delete']);

/**
 * Risk weight per register level (IE=3, Policy=2, Housekeeping=1, else 0).
 * Covers both the original 13 levels and the newer compliance taxonomy
 * (NEW_COMPLIANCE_LEVELS) per the NTCSA-legend associations above.
 */
export function riskWeight(level: string | undefined): number {
  if (!level) return 0;
  if (IE_LEVELS.has(level)) return 3;
  if (POLICY_LEVELS.has(level)) return 2;
  if (HK_LEVELS.has(level)) return 1;
  return 0;
}

/** A transaction "has a finding" when its level carries any risk weight. */
export function isFindingLevel(level: string | undefined): boolean {
  return riskWeight(level) > 0;
}

/**
 * True when a level sits in the Possible-IE (PFMA) tier — self-declared or not.
 *
 * Exported so the drill panel shares this list instead of keeping its own copy.
 * It previously held only the two legacy levels and so under-reported IE for any
 * transaction on the newer "Possible Non-Compliance" taxonomy, making the drill
 * disagree with the tile it drilled into.
 */
export function isPossibleIeLevel(level: string | undefined): boolean {
  return !!level && IE_LEVELS.has(level);
}

export interface DashboardKpis {
  totalTransactions: number;
  reviewed: number;
  /** Reviewed transactions assessed Compliant (no exception). */
  compliant: number;
  /**
   * Reviewed transactions carrying an UNRESOLVED exception — a risk-weighted level
   * that has NOT been self-declared.
   *
   * R7 (Lindiwe, 27 Jul 2026): this used to include self-declarations, which double-
   * counted them as both a gap and its own resolution. Her rule is that "non-compliant
   * is the transactions that have not yet been resolved", so a self-declared
   * transaction now counts in {@link selfDeclared} instead. The reviewed outcomes stay
   * exhaustive: reviewed = compliant + nonCompliant + selfDeclared + anomalies (anomalies = notApplicable + toBeDeleted).
   */
  nonCompliant: number;
  /**
   * Reviewed transactions whose exception was resolved by SELF-DECLARATION. They keep
   * their severity tier (a self-declared possible IE is still IE-weighted) but are
   * reported separately from the unresolved set. Report-side equivalent:
   * `SummaryRow.selfDeclaredGaps`.
   */
  selfDeclared: number;
  /** Reviewed transactions that are Not Applicable (neither compliant nor an exception). */
  notApplicable: number;
  /**
   * Reviewed transactions flagged for removal ('Deleted' / 'To delete') — a lifecycle
   * sentinel, never a verdict. Before 15 Sep 2026 these were counted as Compliant; now
   * they sit apart. Report-side equivalent: `SummaryRow.unclassified`.
   */
  toBeDeleted: number;
  /**
   * The "Anomalies" umbrella (Lindiwe, 15 Sep 2026) = `notApplicable + toBeDeleted`. The
   * reviewed outcomes stay exhaustive: `reviewed = compliant + nonCompliant + selfDeclared
   * + anomalies`. Report-side equivalent: `SummaryRow.notApplicable + unclassified`.
   */
  anomalies: number;
  /** Not-yet-reviewed transactions (population − the four reviewed outcomes). */
  pending: number;
  /** Possible-IE records, BOTH unresolved and self-declared (whole population). */
  possibleIeCount: number;
  /** Possible-IE Rand exposure, BOTH unresolved and self-declared (whole population). */
  possibleIeValue: number;
  /** Possible-IE records still unresolved — not self-declared. */
  possibleIeCountUnresolved: number;
  /**
   * Possible-IE Rand exposure still unresolved — the client slide's
   * "Rand Value - Potential IE based on Unresolved Gaps".
   */
  possibleIeValueUnresolved: number;
  /** Possible-IE records resolved by self-declaration. */
  possibleIeCountSelfDeclared: number;
  /**
   * Possible-IE Rand exposure carried by self-declarations — the client slide's
   * "Rand value - Potential IE to be Self Declared". This is the figure Eskom
   * reports as remaining a risk even though the gap is closed.
   */
  possibleIeValueSelfDeclared: number;
  pendingReviewValue: number;
  /**
   * Records that carry a non-compliant (effective) level REGARDLESS of review — i.e. how
   * many transactions have findings flagged. Distinct from {@link nonCompliant}, which is
   * only the REVIEWED subset AND excludes self-declarations; the difference is
   * flagged-but-not-yet-reviewed records plus self-declared ones.
   */
  recordsWithFindings: number;
  /** Records carrying a self-declared exception, regardless of review progress. */
  recordsSelfDeclared: number;
  recoveryCoveragePct: number;
  riskScore: number;
  byLevel: Record<string, { count: number; value: number }>;
  byMechanism: Record<string, number>;
  byDivision: Record<string, number>;
}

/**
 * Transaction-level KPIs.
 *
 * `effectiveLevelByRef` (optional) maps `referenceNo → effective compliance level`
 * (findings + strict-rule override rolled up — {@link effectiveComplianceByRef}). When
 * supplied, every level-based count reads it INSTEAD of the stored `complianceLevel`,
 * so the dashboard/report figures agree with the Transactions register (which has always
 * shown the effective level). Absent, it falls back to `complianceLevel`.
 *
 * OUTCOME vs REVIEW (Lindiwe, 8 Jul 2026): a compliance OUTCOME only counts once a
 * transaction has been REVIEWED. An unreviewed transaction is Pending even if it already
 * carries provisional finding lines — so `compliant` / `nonCompliant` / `notApplicable`
 * are the REVIEWED subset and, by construction, `reviewed = compliant + nonCompliant +
 * notApplicable`; `pending = total − those three` is exactly the not-yet-reviewed set, so
 * `Population = reviewed + pending`. (The exposure metrics — `possibleIe*`,
 * `recordsWithFindings` — stay whole-population: they measure flagged risk, not review
 * progress.)
 */
export function computeDashboard(
  transactions: Transaction[],
  effectiveLevelByRef?: ReadonlyMap<string, string>,
): DashboardKpis {
  const active = transactions.filter((t) => !t.deleted);
  const total = active.length;

  let reviewed = 0;
  let compliant = 0;
  let nonCompliant = 0;
  let selfDeclared = 0;
  let notApplicable = 0;
  let toBeDeleted = 0;
  let recordsWithFindings = 0;
  let recordsSelfDeclared = 0;
  let possibleIeCount = 0;
  let possibleIeValue = 0;
  let possibleIeCountUnresolved = 0;
  let possibleIeValueUnresolved = 0;
  let possibleIeCountSelfDeclared = 0;
  let possibleIeValueSelfDeclared = 0;
  let pendingReviewValue = 0;
  let weightedRisk = 0;
  const byLevel: Record<string, { count: number; value: number }> = {};
  const byMechanism: Record<string, number> = {};
  const byDivision: Record<string, number> = {};

  for (const t of active) {
    const level = effectiveLevelByRef?.get(t.referenceNo) || t.complianceLevel || 'Pending Review';
    const value = Number(t.value || 0);
    const isReviewed = t.reviewed === true || t.reviewStatus === 'Reviewed';

    // A self-declaration is an exception the buyer resolved by declaring it. It keeps
    // its severity tier — riskWeight is untouched — but is counted apart from the
    // unresolved set (Lindiwe, 27 Jul 2026).
    const selfDeclaredLevel = isSelfDeclaredLevel(level);

    // The outcome split is scoped to REVIEWED transactions and is exhaustive over them
    // (every reviewed row is exactly one of Compliant / Non-Compliant / Self-Declared /
    // N/A, or a delete sentinel), so reviewed === compliant + nonCompliant + selfDeclared
    // + anomalies (anomalies = notApplicable + toBeDeleted).
    // Note "Self-Declared: Not Applicable" carries no risk weight, so it falls to
    // notApplicable rather than selfDeclared — a declared N/A is not a gap.
    if (isReviewed) {
      reviewed++;
      if (isFindingLevel(level)) {
        if (selfDeclaredLevel) selfDeclared++;
        else nonCompliant++;
      } else if (NA_LEVELS.has(level)) notApplicable++;
      else if (level === 'Compliant') compliant++;
      // Reviewed, not a finding, not N/A, not strictly Compliant → an anomaly, chiefly the
      // 'To delete' / 'Deleted' lifecycle sentinels (before 15 Sep 2026 this was a catch-all
      // `else compliant++` that mis-counted them). Mirrors the report's `unclassified`, so
      // dashboard Compliant now provably equals report Compliant.
      else toBeDeleted++;
    }

    // Whole-population risk/exposure (independent of review progress).
    if (isFindingLevel(level)) {
      recordsWithFindings++;
      if (selfDeclaredLevel) recordsSelfDeclared++;
    }
    if (IE_LEVELS.has(level)) {
      possibleIeCount++;
      possibleIeValue += value;
      if (selfDeclaredLevel) {
        possibleIeCountSelfDeclared++;
        possibleIeValueSelfDeclared += value;
      } else {
        possibleIeCountUnresolved++;
        possibleIeValueUnresolved += value;
      }
    }
    if (PENDING_LEVELS.has(level)) pendingReviewValue += value;
    weightedRisk += riskWeight(level);

    byLevel[level] = byLevel[level] ?? { count: 0, value: 0 };
    byLevel[level].count++;
    byLevel[level].value += value;

    const mech = t.mechanism || 'Unspecified';
    byMechanism[mech] = (byMechanism[mech] ?? 0) + 1;
    const div = canonicalDivision(t.division) || 'Unspecified';
    byDivision[div] = (byDivision[div] ?? 0) + 1;
  }

  // Pending = the not-yet-reviewed remainder, so Population = reviewed + pending exactly.
  const pending = total - compliant - nonCompliant - selfDeclared - notApplicable - toBeDeleted;
  // The "Anomalies" umbrella Lindiwe asked for (15 Sep 2026): N/A + flagged-for-removal.
  const anomalies = notApplicable + toBeDeleted;

  return {
    totalTransactions: total,
    reviewed,
    compliant,
    nonCompliant,
    selfDeclared,
    notApplicable,
    toBeDeleted,
    anomalies,
    pending,
    possibleIeCount,
    possibleIeValue,
    possibleIeCountUnresolved,
    possibleIeValueUnresolved,
    possibleIeCountSelfDeclared,
    possibleIeValueSelfDeclared,
    pendingReviewValue,
    recordsWithFindings,
    recordsSelfDeclared,
    recoveryCoveragePct: total > 0 ? Math.round((recordsWithFindings / total) * 100) : 0,
    riskScore: total > 0 ? Math.round((weightedRisk / (total * 3)) * 100) : 0,
    byLevel,
    byMechanism,
    byDivision,
  };
}
